import React, { useState, useMemo, useEffect } from 'react'
import useSWR from 'swr'
import dayjs from 'dayjs'
import { getCoreRowModel, useReactTable } from '@tanstack/react-table'
import type { Row } from '@tanstack/react-table'
import { Card, CardContent } from '@/components/ui/card'
import Loader from '@/components/ui/Loader'
import EmptyDatas from 'components/ui/EmptyDatas'
import Table from 'components/ui/my-data-table'
import MyPagination from 'components/ui/MyPagination'
import {
  columnCover,
  columnPreview,
  columnName,
  columnPainterFrames,
  columnLevel,
  columnTags,
  columnAvatars,
} from 'components/template-data-table-column'
import DisplayUpdateTime from 'ui/DisplayUpdateTime'
import { Button } from '@/components/ui/button'
import { DatePicker } from 'components/ui/date-picker'
import { ContentLangs, ProductOptions } from 'config'
import Selector from 'components/ui/Selector'
import RedisKeyHint from './RedisKeyHint'

type FrameListRow = {
  id: string
  state?: number | null
  contentLangs?: number[]
  createTime?: number
  updateTime?: number
  rankingScore?: number | null
}

type FrameRankingListPage = {
  items: FrameListRow[]
  hasMore: boolean
  pageEndId: string | null
}

type RankingKind = 'daily' | 'total'

const PAGE_SIZE = 50

const countryLangOptions = ContentLangs.filter((l) => l.code > 0)

/** 与 Worker MetricDateUtils 一致：UTC − 8h 业务日 */
function getMetricDailyDate(): Date {
  return dayjs().subtract(8, 'hour').startOf('day').toDate()
}

const FrameRankingLists = ({ kind }: { kind: RankingKind }) => {
  const [lang, setLang] = useState(9)
  const [pid, setPid] = useState(11)
  const [date, setDate] = useState(getMetricDailyDate)
  const [after, setAfter] = useState<string | null>(null)
  const [afters, setAfters] = useState<string[]>([])

  const dateStr = dayjs(date).format('YYYY-MM-DD')

  const basePath =
    kind === 'daily'
      ? `/template-lists/frame-ranking-daily/${lang}`
      : `/template-lists/frame-ranking-total/${lang}`

  const swrParams = useMemo(() => {
    const params: Record<string, string | number> = {
      limit: PAGE_SIZE,
      pid,
      ...(after ? { after } : {}),
    }
    if (kind === 'daily') {
      params.date = dateStr
    }
    return params
  }, [after, pid, kind, dateStr])

  const { data, error, isLoading, mutate } = useSWR<FrameRankingListPage>([
    basePath,
    swrParams,
  ])

  const goAfter = (next: string | null) => {
    setAfter(next)
    if (next === null) {
      setAfters([])
    } else {
      const index = afters.indexOf(next)
      if (index === -1) {
        setAfters([...afters, next])
      } else {
        setAfters(afters.slice(0, index + 1))
      }
    }
  }

  useEffect(() => {
    setAfter(null)
    setAfters([])
  }, [lang, pid, kind, date])

  const nextAfter =
    data?.hasMore && data.pageEndId ? data.pageEndId : null

  const columns = useMemo(
    () => [
      {
        header: '#',
        cell: ({ row }: { row: Row<FrameListRow> }) => row.index + 1,
      },
      {
        header: 'Score',
        id: 'rankingScore',
        cell: ({ row }: { row: Row<FrameListRow> }) => {
          const score = row.original.rankingScore
          return score == null ? '-' : score
        },
      },
      columnCover,
      columnPreview,
      columnName,
      columnPainterFrames,
      columnLevel,
      columnTags,
      columnAvatars,
      {
        header: 'State',
        id: 'onlineState',
        cell: ({ row }: { row: Row<FrameListRow> }) => {
          const d = row.original
          let online: boolean
          if (d.state !== undefined && d.state !== null) {
            online = d.state === 401
          } else {
            const cl = d.contentLangs
            online = Array.isArray(cl) && cl.length > 0
          }
          return (
            <span
              className={
                online
                  ? 'text-green-600 font-medium'
                  : 'text-muted-foreground'
              }
            >
              {online ? '上线' : '下线'}
            </span>
          )
        },
      },
      {
        header: 'CreateTime',
        accessorKey: 'createTime',
        cell: ({ row }: { row: Row<FrameListRow> }) => (
          <DisplayUpdateTime updateTime={row.original.createTime} />
        ),
      },
      {
        header: 'UpdateTime',
        accessorKey: 'updateTime',
        cell: ({ row }: { row: Row<FrameListRow> }) => (
          <DisplayUpdateTime updateTime={row.original.updateTime} />
        ),
      },
    ],
    [],
  )

  const table = useReactTable({
    data: data?.items ?? [],
    columns,
    enableRowSelection: false,
    getRowId: (row) => row.id,
    getCoreRowModel: getCoreRowModel(),
  })

  const redisKey =
    kind === 'daily'
      ? `frame:ranking:daily:reCreate:${dateStr}:lang${lang}:pid${pid}`
      : `frame:ranking:total:reCreate:lang${lang}:pid${pid}`

  const filters = (
    <div className="flex flex-wrap items-center gap-4">
      <div className="flex items-center gap-2">
        <label className="shrink-0 text-sm font-medium">Lang:</label>
        <Selector
          datas={countryLangOptions}
          labelName="Lang"
          value={lang}
          update={setLang}
          showCode
          width="w-[220px]"
        />
      </div>
      <div className="flex items-center gap-2">
        <label className="shrink-0 text-sm font-medium">Pid:</label>
        <Selector
          datas={ProductOptions}
          labelName="Pid"
          value={pid}
          update={setPid}
          showCode
          width="w-[220px]"
        />
      </div>
      {kind === 'daily' ? (
        <div className="flex items-center gap-2">
          <label className="shrink-0 text-sm font-medium">Date:</label>
          <DatePicker
            date={date}
            onSelect={(next: Date | undefined) => {
              if (next) {
                setDate(next)
              }
            }}
          />
        </div>
      ) : null}
      <RedisKeyHint keys={[redisKey]} />
    </div>
  )

  if (isLoading && !data) {
    return (
      <div className="space-y-4">
        {filters}
        <Loader />
      </div>
    )
  }
  if (!data || error) {
    return (
      <div className="space-y-4">
        {filters}
        <EmptyDatas />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex-shrink-0 mb-4">
        <div className="flex flex-wrap items-center gap-4">
          <MyPagination
            nextAfter={nextAfter}
            after={after}
            goAfter={goAfter}
            afters={afters}
            className="w-auto m-0"
          />
          <Button variant="outline" onClick={() => mutate()}>
            Refetch
          </Button>
          {filters}
        </div>
      </div>
      <div className="flex-1 h-[calc(100vh-254px)]">
        <Card className="h-full flex flex-col">
          <CardContent className="flex-1 overflow-hidden pl-4 pr-0 pb-0">
            <div className="h-full overflow-auto">
              {data.items.length === 0 ? (
                <EmptyDatas />
              ) : (
                <Table table={table} />
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

export default FrameRankingLists
